import type { DraftNoteReference } from "@/projectOverview/notes/types";

export interface StoredNoteReference {
  type?: string;
  ref_id?: string | number;
  label?: string | null;
  trigger?: string | null;
  token_text?: string | null;
  start?: number | null;
  end?: number | null;
}

function sortRefs(refs: StoredNoteReference[]): StoredNoteReference[] {
  return [...refs].sort((a, b) => {
    const aStart = Number.isFinite(Number(a?.start)) ? Number(a.start) : Number.MAX_SAFE_INTEGER;
    const bStart = Number.isFinite(Number(b?.start)) ? Number(b.start) : Number.MAX_SAFE_INTEGER;
    return aStart - bStart;
  });
}

/** Positionen von Referenzen im Notiztext auflösen (wie klassisch). */
export function resolveReferencePositions(
  noteText: string,
  references: StoredNoteReference[] = [],
): DraftNoteReference[] {
  const text = String(noteText || "");
  const usedRanges: Array<{ start: number; end: number }> = [];

  return sortRefs(references)
    .map((ref) => {
      const tokenText = String(ref?.token_text || "").trim();
      if (!tokenText) return null;

      let start = Number.isFinite(Number(ref?.start)) ? Number(ref.start) : -1;
      let end = Number.isFinite(Number(ref?.end)) ? Number(ref.end) : -1;

      const validAtPosition =
        start >= 0 &&
        end > start &&
        end <= text.length &&
        text.slice(start, end) === tokenText;

      if (!validAtPosition) {
        const lookupStart = Math.max(0, start >= 0 ? start - 20 : 0);
        start = text.indexOf(tokenText, lookupStart);
        if (start < 0) start = text.indexOf(tokenText);
        end = start >= 0 ? start + tokenText.length : -1;
      }

      if (start < 0 || end <= start) return null;
      if (usedRanges.some((r) => !(end <= r.start || start >= r.end))) return null;
      usedRanges.push({ start, end });

      return {
        type: String(ref?.type || "").trim() as DraftNoteReference["type"],
        ref_id: String(ref?.ref_id || "").trim(),
        label: String(ref?.label || "").trim(),
        trigger: String(ref?.trigger || "").trim(),
        token_text: tokenText,
        start,
        end,
      };
    })
    .filter((r): r is DraftNoteReference => !!r && !!r.type && !!r.ref_id);
}

/** Sichtbarer Token nach Auswahl (Trigger bleibt in `trigger` für die API). */
const NOTE_REF_DISPLAY_PREFIX: Record<string, string | undefined> = {
  "#": "Auftrag ",
  "+": "Bestellung ",
  "*": "Nachweis ",
  "%": "Material ",
  // Dokument-Labels enthalten den Typ bereits („Rechnung R-…“) – kein Präfix.
  "§": "",
};

export function buildNoteReferenceTokenText(trigger: string, label: string): string {
  const trimmedLabel = String(label || "").trim();
  const displayPrefix = NOTE_REF_DISPLAY_PREFIX[trigger];
  if (displayPrefix != null) {
    return `${displayPrefix}${trimmedLabel}`;
  }
  return `${trigger}${trimmedLabel}`;
}

export function getNoteContextAtCursor(
  value: string,
  cursor: number,
): { trigger: string; query: string; start: number; end: number } | null {
  const before = value.slice(0, cursor);
  const match = before.match(/(^|[\s([{\-.,;:])([@#+*%§])([^\s]{0,120})$/);
  if (!match) return null;
  const trigger = match[2];
  if (!trigger) return null;
  const query = match[3] || "";
  if (trigger !== "#" && /[@#+*%§]/.test(query)) return null;
  const start = cursor - query.length - 1;
  return { trigger, query, start, end: cursor };
}

export function shiftReferencesAfter(
  references: DraftNoteReference[],
  position: number,
  delta: number,
): DraftNoteReference[] {
  return references.map((ref) => ({
    ...ref,
    start: ref.start >= position ? ref.start + delta : ref.start,
    end: ref.end >= position ? ref.end + delta : ref.end,
  }));
}

function mergeDeletionRanges(
  ranges: Array<{ start: number; end: number }>,
): Array<{ start: number; end: number }> {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [sorted[0]!];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i]!;
    const last = merged[merged.length - 1]!;
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push(cur);
    }
  }
  return merged;
}

/** Referenz-Block inkl. optionalem Leerzeichen direkt danach (nach Auswahl aus Vorschlägen). */
export function referenceBlockEnd(noteText: string, ref: DraftNoteReference): number {
  let end = ref.end;
  if (noteText[end] === " ") end += 1;
  return end;
}

export type ReferenceDeletionPlan = {
  ranges: Array<{ start: number; end: number }>;
  cursor: number;
};

export function planReferenceDeletion(
  noteText: string,
  references: StoredNoteReference[],
  selectionStart: number,
  selectionEnd: number,
  key: "Backspace" | "Delete",
): ReferenceDeletionPlan | null {
  const resolved = resolveReferencePositions(noteText, references);
  if (!resolved.length) return null;

  const blockRange = (ref: DraftNoteReference) => ({
    start: ref.start,
    end: referenceBlockEnd(noteText, ref),
  });

  if (selectionStart !== selectionEnd) {
    const touched = resolved.filter(
      (r) => r.start < selectionEnd && referenceBlockEnd(noteText, r) > selectionStart,
    );
    if (!touched.length) return null;
    const ranges = mergeDeletionRanges([
      { start: selectionStart, end: selectionEnd },
      ...touched.map(blockRange),
    ]);
    return { ranges, cursor: ranges[0]?.start ?? selectionStart };
  }

  const pos = selectionStart;

  if (key === "Backspace") {
    if (pos === 0) return null;
    const ref = resolved.find((r) => {
      const blockEnd = referenceBlockEnd(noteText, r);
      return pos > r.start && pos <= blockEnd;
    });
    if (!ref) return null;
    const range = blockRange(ref);
    return { ranges: [range], cursor: range.start };
  }

  const ref = resolved.find((r) => {
    const blockEnd = referenceBlockEnd(noteText, r);
    return pos >= r.start && pos < blockEnd;
  });
  if (!ref) return null;
  const range = blockRange(ref);
  return { ranges: [range], cursor: range.start };
}

export function applyReferenceDeletionPlan(
  noteText: string,
  references: DraftNoteReference[],
  plan: ReferenceDeletionPlan,
): { noteText: string; references: DraftNoteReference[] } {
  const sorted = [...plan.ranges].sort((a, b) => b.start - a.start);
  let text = noteText;
  let refs = references;

  for (const range of sorted) {
    const len = range.end - range.start;
    const resolved = resolveReferencePositions(text, refs);
    refs = resolved
      .filter((r) => !(r.end > range.start && referenceBlockEnd(text, r) > range.start))
      .map((r) => {
        const blockEnd = referenceBlockEnd(text, r);
        if (r.start >= range.end) {
          return { ...r, start: r.start - len, end: r.end - len };
        }
        if (blockEnd <= range.start) {
          return r;
        }
        return null;
      })
      .filter((r): r is DraftNoteReference => r != null);
    text = text.slice(0, range.start) + text.slice(range.end);
  }

  return { noteText: text, references: refs };
}

export function prepareReferencesForSave(
  noteText: string,
  references: DraftNoteReference[],
): DraftNoteReference[] {
  return resolveReferencePositions(noteText, references);
}

export interface NoteBodySegment {
  kind: "text" | "ref";
  text: string;
  ref?: DraftNoteReference;
}

export function splitNoteBodySegments(
  noteText: string,
  references: StoredNoteReference[] = [],
): NoteBodySegment[] {
  const resolved = resolveReferencePositions(noteText, references);
  if (!resolved.length) return [{ kind: "text", text: noteText }];

  const parts: NoteBodySegment[] = [];
  let cursor = 0;
  for (const ref of resolved) {
    if (ref.start > cursor) {
      parts.push({ kind: "text", text: noteText.slice(cursor, ref.start) });
    }
    parts.push({
      kind: "ref",
      text: noteText.slice(ref.start, ref.end),
      ref,
    });
    cursor = ref.end;
  }
  if (cursor < noteText.length) {
    parts.push({ kind: "text", text: noteText.slice(cursor) });
  }
  return parts;
}
